import React, { useMemo } from "react";
import { Table } from "antd";
import { formatSearchDate } from "../search/searchResultUtils";
import { resolveCheckinTypeLabel } from "./checkinTypeLabel";
import { SORT_BY, withServerSorter, sorterToQuery } from "../../utils/tableSortParams";
import {
  dateRangeFilterDropdown,
  numericRangeFilterDropdown,
  parseTableRangeFilter,
  rangeFilterValue,
  toDayjsRange,
} from "./paQueueTableUtils";

function formatPatientName(claim) {
  const surname = claim?.surname?.__capitalize?.() || claim?.surname || "";
  const first = claim?.first_name?.__capitalize?.() || claim?.first_name || "";
  const middle = claim?.middle_name?.__capitalize?.() || claim?.middle_name || "";
  return `${surname}${surname ? ", " : ""}${first} ${middle}`.trim() || "—";
}

function formatLabel(value) {
  if (!value) return "—";
  return value.__capitalize?.() || value;
}

function formatNaira(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `₦${n.toLocaleString()}`;
}

const FIELD_TO_SORT_BY = {
  checkin_date: SORT_BY.CHECKIN_DATE,
  items: SORT_BY.ITEMS,
  pa_items_count: SORT_BY.ITEMS,
  amount: SORT_BY.AMOUNT,
  requested: SORT_BY.REQUESTED,
  requested_total: SORT_BY.REQUESTED,
  approved: SORT_BY.APPROVED,
  approved_total: SORT_BY.APPROVED,
  tat: SORT_BY.TAT,
};

function PAQueueTable({
  data,
  onOpenClaim,
  sortBy,
  sortDir,
  onSortChange,
  onFilterChange,
  from,
  to,
  onDateRangeChange,
  itemsMin,
  itemsMax,
  requestedMin,
  requestedMax,
  approvedMin,
  approvedMax,
}) {
  const sort = { sort_by: sortBy, sort_dir: sortDir };
  const dateValue = toDayjsRange(from, to);
  const columns = useMemo(
    () => [
      {
        title: "Patient",
        key: "patient",
        ellipsis: true,
        render: (_, record) => (
          <div className="preauth-table__patient">
            <span className="preauth-table__patient-name">{formatPatientName(record)}</span>
            <span className="preauth-table__patient-meta">{record?.code || "—"}</span>
          </div>
        ),
      },
      withServerSorter(
        {
          title: "Check-in",
          key: "checkin_date",
          width: 132,
          responsive: ["sm"],
          sortDirections: ["descend", "ascend"],
          filteredValue: from || to ? [true] : null,
          filterDropdown: dateRangeFilterDropdown({
            value: dateValue,
            onChange: onDateRangeChange,
          }),
          render: (_, record) => formatSearchDate(record?.checkin_date),
        },
        SORT_BY.CHECKIN_DATE,
        sort
      ),
      {
        title: "Encounter type",
        key: "checkin_type_name",
        width: 150,
        ellipsis: true,
        render: (_, record) => {
          const type = resolveCheckinTypeLabel(record);
          if (!type) return <span className="preauth-table__muted">—</span>;
          return <span className="preauth-table__type">{type}</span>;
        },
      },
      {
        title: "Facility",
        key: "facility_name",
        ellipsis: true,
        responsive: ["md"],
        render: (_, record) => formatLabel(record?.facility_name),
      },
      {
        title: "Plan",
        key: "plan_name",
        ellipsis: true,
        responsive: ["lg"],
        render: (_, record) => {
          const plan = record?.plan_name || record?.insurance_package;
          if (!plan) return <span className="preauth-table__muted">—</span>;
          return formatLabel(plan);
        },
      },
      withServerSorter(
        {
          title: "Items",
          dataIndex: "pa_items_count",
          key: "items",
          width: 88,
          align: "right",
          sortDirections: ["descend", "ascend"],
          filteredValue: rangeFilterValue(itemsMin, itemsMax),
          filterDropdown: numericRangeFilterDropdown({
            minPlaceholder: "Min items",
            maxPlaceholder: "Max items",
          }),
          render: (v, record) => (record?.pa_items_count != null ? record.pa_items_count : v != null ? v : "—"),
        },
        SORT_BY.ITEMS,
        sort
      ),
      withServerSorter(
        {
          title: "Requested",
          key: "requested",
          width: 110,
          align: "right",
          responsive: ["sm"],
          sortDirections: ["descend", "ascend"],
          filteredValue: rangeFilterValue(requestedMin, requestedMax),
          filterDropdown: numericRangeFilterDropdown({
            minPlaceholder: "Min ₦",
            maxPlaceholder: "Max ₦",
            prefix: "₦",
          }),
          render: (_, record) => formatNaira(record?.requested_total ?? record?.total_requested_cost ?? record?.amount),
        },
        SORT_BY.REQUESTED,
        sort
      ),
      withServerSorter(
        {
          title: "Approved",
          key: "approved",
          width: 110,
          align: "right",
          responsive: ["lg"],
          sortDirections: ["descend", "ascend"],
          filteredValue: rangeFilterValue(approvedMin, approvedMax),
          filterDropdown: numericRangeFilterDropdown({
            minPlaceholder: "Min ₦",
            maxPlaceholder: "Max ₦",
            prefix: "₦",
          }),
          render: (_, record) => formatNaira(record?.approved_total ?? record?.total_approved_cost),
        },
        SORT_BY.APPROVED,
        sort
      ),
      {
        title: "Flags",
        key: "flags",
        width: 64,
        align: "center",
        responsive: ["lg"],
        render: (_, record) => {
          const count = record?.total_flags || record?.flags?.active_flags_count || 0;
          const severity = record?.highest_severity || record?.flags_highest_severity || "";
          if (!count && !record?.has_active_flags) {
            return <span className="preauth-table__muted">—</span>;
          }
          return (
            <span
              className={`preauth-table__flags${severity ? ` preauth-table__flags--${severity}` : ""}`}
              title={severity ? `${severity} flag` : "Active flags"}
            >
              {count || "!"}
            </span>
          );
        },
      },
    ],
    [
      sort.sort_by,
      sort.sort_dir,
      from,
      to,
      dateValue,
      onDateRangeChange,
      itemsMin,
      itemsMax,
      requestedMin,
      requestedMax,
      approvedMin,
      approvedMax,
    ]
  );

  return (
    <Table
      className="preauth-queue-table search-results-table"
      columns={columns}
      dataSource={data}
      rowKey={(record) => record?.code || record?.checkin_id || record?.id}
      pagination={false}
      size="middle"
      onChange={(_pagination, filters, sorter) => {
        const s = Array.isArray(sorter) ? sorter[0] : sorter;
        const next = sorterToQuery(s, FIELD_TO_SORT_BY);
        const items = parseTableRangeFilter(filters, "items");
        const requested = parseTableRangeFilter(filters, "requested");
        const approved = parseTableRangeFilter(filters, "approved");
        const patch = {
          sort_by: next.sort_by || SORT_BY.CHECKIN_DATE,
          sort_dir: next.sort_dir || "desc",
          items_min: items.min ?? "",
          items_max: items.max ?? "",
          requested_min: requested.min ?? "",
          requested_max: requested.max ?? "",
          approved_min: approved.min ?? "",
          approved_max: approved.max ?? "",
        };
        onSortChange?.(patch);
        onFilterChange?.(patch);
      }}
      onRow={(record) => ({
        onClick: () => onOpenClaim?.(record),
      })}
    />
  );
}

export { PAQueueTable };
export default PAQueueTable;
